/**
 * Who this page is compared against, and why.
 *
 * COMPARE_FIRST_URL_REVIEW.md §3.1/§3.2: the review auto-selects a compare
 * target instead of handing the operator an empty dropdown, and the default is
 * the **SERP winner** — whoever took the position on this page's own query,
 * marked competitor or not. The topical peer stays as an override because
 * "who is closest to my topic" is a real and different question, but it is not
 * the default: the review's framing is "you lost ground", and the page that
 * took the ground is the answer to that.
 *
 * Pure on purpose, and that is a correctness requirement rather than a testing
 * convenience. `top-serp-rivals.ts` picks the rival it *crawls* with these
 * exact rules, so the target this resolver names is the one page we hold an
 * analysis for. A second implementation that drifted by one guard would have
 * the review pointing at a page it has no numbers for — the failure mode is
 * silent and looks like missing data.
 *
 * Keep in sync with `backend/src/utils/compare-target.ts` (verbatim copy;
 * the frontend cannot import across the workspace boundary).
 */

import { isNonCompetitorDomain } from './non-competitor-domains';
import {
  formatWeakSerpSkipNote,
  weakSerpCompareReason,
  type WeakSerpCompareReason
} from './weak-serp-compare-target';

export interface CompareSerpRow {
  position: number;
  domain: string;
  url: string;
  title?: string;
  description?: string;
}

/**
 * `serp_winner` is the default target. `topical_peer` is the override, and it
 * is deliberately a different kind rather than a lower-ranked winner: the two
 * answer different questions and the row has to say which one it is showing.
 */
export type CompareTargetKind = 'serp_winner' | 'topical_peer';

export interface CompareTarget {
  url: string;
  domain: string;
  /** SERP position, or null for a topical peer that does not rank on this query. */
  position: number | null;
  kind: CompareTargetKind;
  /** Shown in the review: why THIS page was chosen. Never empty. */
  reason: string;
  /** True when the operator has marked this domain a competitor. Not a filter. */
  isMarkedCompetitor: boolean;
}

export interface CompareTargetResolution {
  /** Best first. Empty when nothing on this SERP can be compared. */
  targets: CompareTarget[];
  /**
   * Why higher-ranked rows were passed over (homepage / login / app-shell).
   * Null when nothing was skipped. Surfaced so "why not #1?" has an answer.
   */
  skippedNote: string | null;
  /**
   * Set when there is no target *because you already hold the top slot*. That
   * is a result, not missing data, and the two must not share a rendering.
   */
  youAreTop: boolean;
}

export function normalizeCompareDomain(value: string): string {
  return (value || '').toLowerCase().trim().replace(/^www\./, '');
}

/**
 * Item 1: the page each domain ranks with on THIS query.
 *
 * Not the same as that domain's topical peer, which is what Supporting pages
 * answers. A domain can hold several slots; the best-ranked one is the page it
 * is actually winning with, so that is the one worth comparing against.
 *
 * Scope is honestly the snapshot we hold — the top 10. A marked competitor
 * absent from it has no ranking page for this query, and the map says so by
 * omitting them rather than by falling back to some other page of theirs.
 */
export function bestRankingPageByDomain(
  results: readonly CompareSerpRow[] | undefined | null
): Map<string, CompareSerpRow> {
  const best = new Map<string, CompareSerpRow>();
  for (const row of results || []) {
    const domain = normalizeCompareDomain(row?.domain || '');
    const url = (row?.url || '').trim();
    if (!domain || !url) continue;
    if (typeof row.position !== 'number' || !Number.isFinite(row.position)) continue;
    const current = best.get(domain);
    if (!current || row.position < current.position) best.set(domain, row);
  }
  return best;
}

export interface ResolveCompareTargetsInput {
  results: readonly CompareSerpRow[] | undefined | null;
  /** Your own domain — never a compare target. */
  selfDomain: string;
  /** Your rank on this query; null when you are not in the top 10. */
  yourPosition: number | null;
  /** Marked competitor domains. Annotates targets; it does not gate them. */
  markedDomains?: readonly string[];
  /**
   * The topical peer per domain, from the Supporting pages snapshot
   * (`focalMatchScore` hero). Offered as an override target, never as default.
   */
  topicalPeers?: readonly { domain: string; url: string }[];
}

/**
 * The ordered target list. `targets[0]` is what the review opens on.
 *
 * The SERP filter is the one `selectTopSerpRivals` crawls with, guard for
 * guard, including "must actually beat you":
 *
 *   - your own domain is not a rival
 *   - social / UGC platforms are not rivals (`isNonCompetitorDomain`)
 *   - homepages, login walls and app shells are not comparable pages
 *     (`weakSerpCompareReason`) — a content comparison against one produces a
 *     confident-looking answer about nothing
 *   - a row at or below your own position did not take anything from you
 *
 * That last guard is why `youAreTop` exists rather than an empty list: at #1
 * there is no winner to compare against because you are the winner, and
 * rendering that the same as "no SERP data" would report a win as a blank.
 */
export function resolveCompareTargets(
  input: ResolveCompareTargetsInput
): CompareTargetResolution {
  const self = normalizeCompareDomain(input.selfDomain);
  const marked = new Set((input.markedDomains || []).map(normalizeCompareDomain).filter(Boolean));
  const empty: CompareTargetResolution = { targets: [], skippedNote: null, youAreTop: false };

  if (!self || !input.results?.length) return empty;
  if (input.yourPosition === 1) return { ...empty, youAreTop: true };

  const skipped: WeakSerpCompareReason[] = [];
  const serpTargets: CompareTarget[] = [];
  const seen = new Set<string>();

  // Best-ranked first, so `targets[0]` is the winner regardless of array order.
  const ordered = [...input.results]
    .filter((row) => typeof row?.position === 'number' && Number.isFinite(row.position))
    .sort((a, b) => a.position - b.position);

  for (const row of ordered) {
    const domain = normalizeCompareDomain(row.domain || '');
    const url = (row.url || '').trim();
    if (!domain || !url || domain === self) continue;
    if (isNonCompetitorDomain(domain)) continue;
    if (input.yourPosition !== null && row.position >= input.yourPosition) continue;

    const weak = weakSerpCompareReason(url, { title: row.title, description: row.description });
    if (weak) {
      skipped.push(weak);
      continue;
    }
    // One target per domain: their best-ranked page is the one they are
    // winning with, and two slots from one domain is one rival, not two.
    if (seen.has(domain)) continue;
    seen.add(domain);

    serpTargets.push({
      url,
      domain,
      position: row.position,
      kind: 'serp_winner',
      reason: compareTargetReason(row.position, input.yourPosition),
      isMarkedCompetitor: marked.has(domain)
    });
  }

  const targets = [...serpTargets];

  // Topical peers land after every SERP target: they answer the other question,
  // and a peer offered above the page that actually took the position would put
  // the override where the default belongs.
  for (const peer of input.topicalPeers || []) {
    const domain = normalizeCompareDomain(peer?.domain || '');
    const url = (peer?.url || '').trim();
    if (!domain || !url || domain === self) continue;
    if (targets.some((t) => t.url === url)) continue;
    targets.push({
      url,
      domain,
      position: null,
      kind: 'topical_peer',
      reason: 'Closest page to your focal topics on this domain',
      isMarkedCompetitor: marked.has(domain)
    });
  }

  return { targets, skippedNote: formatWeakSerpSkipNote(skipped), youAreTop: false };
}

/**
 * Why this page is the target, in the operator's terms.
 *
 * Three genuinely different situations, so three sentences. "Ranks #1" and
 * "ranks above you" are not the same claim, and neither is "ranks while you do
 * not rank at all" — collapsing them into one safe phrase loses the only part
 * an operator acts on.
 */
export function compareTargetReason(position: number, yourPosition: number | null): string {
  if (yourPosition === null) {
    return position === 1
      ? 'Ranks #1 for this page’s query; you are not in the top 10'
      : `Ranks #${position} for this page’s query; you are not in the top 10`;
  }
  if (position === 1) return `Ranks #1 for this page’s query; you hold #${yourPosition}`;
  return `Ranks #${position} for this page’s query, above your #${yourPosition}`;
}
